import { createSlice, createSelector } from '@reduxjs/toolkit';
import { fetchSolutionsByPostId, fetchSolutionById, fetchSavedSolutions, fetchSolutionsFirstPage, fetchMoreSolutionsByPostId, toggleShareSolution } from './solutionThunks/index.js';
import { generatePhaseId, generateTaskId, resetIdCounters } from '../../utils/idGenerator.js';

// Helper function to create a new phase with default values
const createNewPhase = (phaseNumber = 1) => ({
  id: generatePhaseId(),
  objective: '',
  phaseNumber,
  tasks: [{
    id: generateTaskId(),
    title: '',
    content: '',
    taskNumber: 1
  }]
});

// Initial state structure
const initialState = {
  // Solutions organized by post ID
  byPostId: {
    // postId: { solutions: [], loading: false, loadingMore: false, error: null, lastUpdated: null, hasMore: true, lastCursor: null, pageSize: 10 }
  },

  // Current solution being viewed/edited
  current: {
    solution: null,
    loading: false,
    error: null,
    upvoting: false,
    upvoteError: null
  },

  // Builder state for creating/editing solutions
  builder: {
    solution: {
      title: '',
      type: '',
      description: '',
      content: '',
      imageUrls: [],
      postId: null
    },
    loading: false,
    error: null,
    phases: [createNewPhase(1)],
    currentPhaseIndex: 0,
    lastUpdated: null
  },

  // Active subscriptions
  subscriptions: {},

  // Saved solutions for current user
  saved: {
    solutions: [],
    loading: false,
    error: null
  },
  // Listener Guard: Track unconfirmed shares
  pendingShareTicks: {}, // { solutionId: number }
};

/**
 * Solutions Redux Slice
 * 
 * Manages state for:
 * - Solution viewing/display
 * - Solution creation/editing
 * - Phase/task management
 * - Upvoting/subscriptions
 * 
 * Key Features:
 * - Per-post solution organization
 * - Builder state isolation
 * - Immutable state updates
 * - Real-time sync with Firestore
 */
const solutionSlice = createSlice({
  name: 'solutions',
  initialState,
  reducers: {
    // Clear all solutions for a specific post
    clearSolutionsForPost: (state, action) => {
      const { postId } = action.payload;
      if (state.byPostId[postId]) {
        state.byPostId[postId] = {
          solutions: [],
          loading: false,
          loadingMore: false,
          error: null,
          lastUpdated: null,
          hasMore: true,
          lastCursor: null,
          pageSize: state.byPostId[postId].pageSize || 10
        };
      }
    },

    // Clear current solution
    clearCurrentSolution: (state) => {
      state.current = { ...initialState.current };
    },

    // Set upvote status for current user
    setUpvoteStatus: (state, action) => {
      const { hasUpvoted, userId } = action.payload;
      if (state.current.solution) {
        state.current.solution.hasUpvoted = hasUpvoted;

        // Update upvotes count
        if (hasUpvoted) {
          state.current.solution.upvotes = (state.current.solution.upvotes || 0) + 1;
          state.current.solution.voters = [...(state.current.solution.voters || []), userId];
        } else {
          state.current.solution.upvotes = Math.max(0, (state.current.solution.upvotes || 1) - 1);
          state.current.solution.voters = (state.current.solution.voters || []).filter(id => id !== userId);
        }
      }
    },

    // Set like status for current user
    setLikeStatus: (state, action) => {
      const { liked, userId, solutionId, postId } = action.payload;
      // Update likes on the current solution (idempotent)
      if (state.current.solution) {
        const already = Array.isArray(state.current.solution.likers) && state.current.solution.likers.includes(userId);
        if (liked) {
          if (!already) {
            state.current.solution.likes = (state.current.solution.likes || 0) + 1;
            state.current.solution.likers = [...(state.current.solution.likers || []), userId];
          }
        } else {
          if (already) {
            state.current.solution.likes = Math.max(0, (state.current.solution.likes || 1) - 1);
            state.current.solution.likers = (state.current.solution.likers || []).filter(id => id !== userId);
          }
        }
      }

      // Update likes inside the list for the corresponding post
      const applyListUpdate = (solutions) => {
        if (!Array.isArray(solutions)) return;
        const targetId = solutionId || state.current.solution?.id;
        if (!targetId) return;
        const idx = solutions.findIndex(s => s && s.id === targetId);
        if (idx >= 0) {
          const sol = solutions[idx];
          const inArray = Array.isArray(sol.likers) && sol.likers.includes(userId);
          if (liked && !inArray) {
            sol.likes = (sol.likes || 0) + 1;
            sol.likers = [...(sol.likers || []), userId];
          } else if (!liked && inArray) {
            sol.likes = Math.max(0, (sol.likes || 1) - 1);
            sol.likers = (sol.likers || []).filter(id => id !== userId);
          }
        }
      };

      if (solutionId) {
        if (postId && state.byPostId[postId]?.solutions) {
          applyListUpdate(state.byPostId[postId].solutions);
          state.byPostId[postId].lastUpdated = new Date().toISOString();
        } else {
          Object.keys(state.byPostId).forEach(pid => {
            if (state.byPostId[pid]?.solutions) {
              applyListUpdate(state.byPostId[pid].solutions);
              state.byPostId[pid].lastUpdated = new Date().toISOString();
            }
          });
        }
      }
    },

    // Set save status for current user
    setSaveStatus: (state, action) => {
      const { saved, userId, solutionId, postId } = action.payload;
      // Update current solution (idempotent)
      if (state.current.solution) {
        const already = Array.isArray(state.current.solution.savers) && state.current.solution.savers.includes(userId);
        if (saved) {
          if (!already) {
            state.current.solution.saves = (state.current.solution.saves || 0) + 1;
            state.current.solution.savers = [...(state.current.solution.savers || []), userId];
          }
        } else {
          if (already) {
            state.current.solution.saves = Math.max(0, (state.current.solution.saves || 1) - 1);
            state.current.solution.savers = (state.current.solution.savers || []).filter(id => id !== userId);
          }
        }
      }

      // Update list-level (idempotent)
      const applyListUpdate = (solutions) => {
        if (!Array.isArray(solutions)) return;
        const targetId = solutionId || state.current.solution?.id;
        if (!targetId) return;
        const idx = solutions.findIndex(s => s && s.id === targetId);
        if (idx >= 0) {
          const sol = solutions[idx];
          const inArray = Array.isArray(sol.savers) && sol.savers.includes(userId);
          if (saved && !inArray) {
            sol.saves = (sol.saves || 0) + 1;
            sol.savers = [...(sol.savers || []), userId];
          } else if (!saved && inArray) {
            sol.saves = Math.max(0, (sol.saves || 1) - 1);
            sol.savers = (sol.savers || []).filter(id => id !== userId);
          }
        }
      };

      if (solutionId) {
        if (postId && state.byPostId[postId]?.solutions) {
          applyListUpdate(state.byPostId[postId].solutions);
          state.byPostId[postId].lastUpdated = new Date().toISOString();
        } else {
          Object.keys(state.byPostId).forEach(pid => {
            if (state.byPostId[pid]?.solutions) {
              applyListUpdate(state.byPostId[pid].solutions);
              state.byPostId[pid].lastUpdated = new Date().toISOString();
            }
          });
        }
      }
    },

    // Set share status for current user
    setShareStatus: (state, action) => {
      const { shared, userId, solutionId, postId } = action.payload;

      // Listener Guard: Increment pending ticks for optimistic update
      if (shared) {
        state.pendingShareTicks[solutionId] = (state.pendingShareTicks[solutionId] || 0) + 1;
      }

      // Update current solution (Non-idempotent: Total Share Events)
      if (state.current.solution && state.current.solution.id === solutionId) {
        state.current.solution.shares = (state.current.solution.shares || 0) + 1;
        state.current.solution.sharers = [...(state.current.solution.sharers || []), userId];
      }

      // Update list-level (idempotent)
      const applyListUpdate = (solutions) => {
        if (!Array.isArray(solutions)) return;
        const targetId = solutionId || state.current.solution?.id;
        if (!targetId) return;
        const idx = solutions.findIndex(s => s && s.id === targetId);
        if (idx >= 0) {
          const sol = solutions[idx];
          if (shared) {
            sol.shares = (sol.shares || 0) + 1;
            sol.sharers = [...(sol.sharers || []), userId];
          } else {
            // share=false case (unlikely for solutions but handled for completeness)
            sol.shares = Math.max(0, (sol.shares || 1) - 1);
            sol.sharers = (sol.sharers || []).filter(id => id !== userId);
          }
        }
      };

      if (solutionId) {
        if (postId && state.byPostId[postId]?.solutions) {
          applyListUpdate(state.byPostId[postId].solutions);
          state.byPostId[postId].lastUpdated = new Date().toISOString();
        } else {
          Object.keys(state.byPostId).forEach(pid => {
            if (state.byPostId[pid]?.solutions) {
              applyListUpdate(state.byPostId[pid].solutions);
              state.byPostId[pid].lastUpdated = new Date().toISOString();
            }
          });
        }
      }
    },

    // Saved solutions list updates (optimistic)
    addToSavedSolutions: (state, action) => {
      const solution = action.payload;
      if (!solution || !solution.id) return;
      if (!Array.isArray(state.saved.solutions)) {
        state.saved.solutions = [];
      }
      const idx = state.saved.solutions.findIndex(s => s.id === solution.id);
      if (idx >= 0) {
        state.saved.solutions[idx] = { ...state.saved.solutions[idx], ...solution };
      } else {
        state.saved.solutions = [solution, ...state.saved.solutions];
      }
    },
    removeFromSavedSolutions: (state, action) => {
      const { solutionId } = action.payload || {};
      if (!solutionId || !Array.isArray(state.saved.solutions)) return;
      state.saved.solutions = state.saved.solutions.filter(s => s.id !== solutionId);
    },

    // Builder actions
    updateSolutionField: (state, action) => {
      const { field, value } = action.payload;

      // Ensure we have a solution object in the builder state
      if (!state.builder.solution) {
        state.builder.solution = {};
      }

      // Create a new solution object to ensure proper state updates
      state.builder.solution = {
        ...state.builder.solution, // Preserve existing solution properties
        [field]: value, // Update the specified field
        updatedAt: new Date().toISOString() // Track when the solution was last updated
      };
    },

    addPhase: (state, action) => {
      const { objective, taskTitle, taskContent } = action.payload || {};
      const currentPhases = state.builder.phases ? [...state.builder.phases] : [];
      const newPhase = {
        id: `phase-${Date.now()}`,
        objective: objective || '',
        phaseNumber: currentPhases.length + 1,
        tasks: [{
          id: `task-${Date.now()}`,
          description: '', // Legacy field
          content: taskContent || '',
          title: taskTitle || '',
          taskNumber: 1
        }]
      };

      state.builder.phases = [...currentPhases, newPhase];
      state.builder.currentPhaseIndex = currentPhases.length;
    },

    updatePhase: (state, action) => {
      const { index, field, value } = action.payload;

      if (state.builder.phases[index]) {
        if (field === 'tasks') {
          state.builder.phases[index].tasks = value;
        } else {
          state.builder.phases[index][field] = value;
        }
      }
    },

    addTask: (state, action) => {
      const { phaseIndex, title, content } = action.payload;
      const phase = state.builder.phases[phaseIndex];

      if (phase) {
        phase.tasks.push({
          id: `task-${Date.now()}`,
          title: title || '',
          content: content || '',
          taskNumber: phase.tasks.length + 1
        });
      }
    },

    updateTask: (state, action) => {
      const { phaseIndex, taskIndex, updates } = action.payload;

      // Create a deep copy of the phases array
      const newPhases = JSON.parse(JSON.stringify(state.builder.phases));

      // Update the specific task
      if (newPhases[phaseIndex]?.tasks?.[taskIndex]) {
        // Ensure we always use content field, falling back to description if it exists
        const content = updates.content !== undefined
          ? updates.content
          : (updates.description || newPhases[phaseIndex].tasks[taskIndex].content || newPhases[phaseIndex].tasks[taskIndex].description || '');

        newPhases[phaseIndex].tasks[taskIndex] = {
          ...newPhases[phaseIndex].tasks[taskIndex],
          ...updates,
          content: content
        };

        // Remove description field if it exists to avoid confusion
        if (newPhases[phaseIndex].tasks[taskIndex].description) {
          delete newPhases[phaseIndex].tasks[taskIndex].description;
        }

        // Update the state with the new phases array
        state.builder.phases = newPhases;
      }
    },

    removePhase: (state, action) => {
      const { phaseIndex } = action.payload;
      if (state.builder.phases.length > 0) {
        state.builder.phases.splice(phaseIndex, 1);
        // Update phase numbers after removal
        state.builder.phases = state.builder.phases.map((phase, index) => ({
          ...phase,
          phaseNumber: index + 1
        }));
        state.builder.currentPhaseIndex = Math.min(
          state.builder.currentPhaseIndex,
          Math.max(0, state.builder.phases.length - 1)
        );
      }
    },

    removeTask: (state, action) => {
      const { phaseIndex, taskIndex } = action.payload;
      const phase = state.builder.phases[phaseIndex];
      if (phase?.tasks?.[taskIndex]) {
        phase.tasks.splice(taskIndex, 1);
        // Update task numbers after removal
        phase.tasks = phase.tasks.map((task, index) => ({
          ...task,
          taskNumber: index + 1
        }));
      }
    },

    resetBuilder: (state) => {
      // Reset builder state while preserving subscriptions
      state.builder = {
        ...initialState.builder,
        // Keep any existing subscription data
        subscriptions: state.subscriptions,
        // Reset with empty phases
        phases: [],
        currentPhaseIndex: 0
      };

      // Reset ID counters for clean state
      resetIdCounters();

      // Clear any errors
      state.builder.error = null;
      state.error = null;
    },

    // Clear all solution data
    resetSolutions: (state) => {
      return { ...initialState };
    },

    // Set unsubscribe function for a post
    setUnsubscribe: (state, action) => {
      const { postId, unsubscribe } = action.payload;
      if (unsubscribe === null) {
        delete state.subscriptions[postId];
      } else {
        state.subscriptions[postId] = unsubscribe;
      }
    }
  },
  extraReducers: (builder) => {
    // Paginated: first page
    builder.addCase(fetchSolutionsFirstPage.pending, (state, action) => {
      const { postId } = action.meta.arg || {};
      if (!state.byPostId[postId]) {
        state.byPostId[postId] = { solutions: [], loading: true, loadingMore: false, error: null, lastUpdated: null, hasMore: true, lastCursor: null, pageSize: action.meta.arg?.pageSize || 10 };
      } else {
        state.byPostId[postId].loading = true;
        state.byPostId[postId].error = null;
        state.byPostId[postId].pageSize = action.meta.arg?.pageSize || state.byPostId[postId].pageSize || 10;
      }
    });

    builder.addCase(fetchSolutionsFirstPage.fulfilled, (state, action) => {
      const { postId, solutions, lastCursor, hasMore, pageSize, total } = action.payload || {};
      if (!postId) return;
      state.byPostId[postId] = state.byPostId[postId] || {};
      state.byPostId[postId].solutions = (Array.isArray(solutions) ? solutions : []).map(s => ({
        ...s,
        shares: (s.shares || 0) + (state.pendingShareTicks[s.id] || 0)
      }));
      state.byPostId[postId].loading = false;
      state.byPostId[postId].error = null;
      state.byPostId[postId].lastUpdated = new Date().toISOString();
      state.byPostId[postId].hasMore = Boolean(hasMore);
      state.byPostId[postId].lastCursor = lastCursor || null;
      state.byPostId[postId].pageSize = pageSize || state.byPostId[postId].pageSize || 10;
      // Persist total count from server for consistent header counts
      if (typeof total === 'number') {
        state.byPostId[postId].total = total;
      } else if (typeof state.byPostId[postId].total !== 'number') {
        state.byPostId[postId].total = state.byPostId[postId].solutions.length;
      }
    });

    builder.addCase(fetchSolutionsFirstPage.rejected, (state, action) => {
      const { postId } = action.meta.arg || {};
      if (!postId) return;
      state.byPostId[postId] = state.byPostId[postId] || {};
      state.byPostId[postId].loading = false;
      state.byPostId[postId].error = action.payload || action.error.message || 'Failed to fetch solutions';
    });

    // Paginated: load more
    builder.addCase(fetchMoreSolutionsByPostId.pending, (state, action) => {
      const { postId } = action.meta.arg || {};
      if (!postId) return;
      state.byPostId[postId] = state.byPostId[postId] || {};
      state.byPostId[postId].loadingMore = true;
      state.byPostId[postId].error = null;
    });

    builder.addCase(fetchMoreSolutionsByPostId.fulfilled, (state, action) => {
      const { postId, solutions, lastCursor, hasMore, total } = action.payload || {};
      if (!postId) return;
      const existing = state.byPostId[postId]?.solutions || [];
      // De-duplicate by id when appending
      const incoming = Array.isArray(solutions) ? solutions : [];
      const seen = new Set(existing.map(s => s.id));
      const appended = incoming.filter(s => s && !seen.has(s.id));
      state.byPostId[postId].solutions = existing.concat(appended);
      state.byPostId[postId].loadingMore = false;
      state.byPostId[postId].loading = false;
      state.byPostId[postId].error = null;
      state.byPostId[postId].lastCursor = lastCursor || state.byPostId[postId].lastCursor || null;
      state.byPostId[postId].hasMore = Boolean(hasMore);
      state.byPostId[postId].lastUpdated = new Date().toISOString();
      // Update total count if provided
      if (typeof total === 'number') {
        state.byPostId[postId].total = total;
      }
    });

    builder.addCase(fetchMoreSolutionsByPostId.rejected, (state, action) => {
      const { postId } = action.meta.arg || {};
      if (!postId) return;
      state.byPostId[postId].loadingMore = false;
      state.byPostId[postId].error = action.payload || action.error.message || 'Failed to load more solutions';
    });

    // Handle fetchSolutionsByPostId
    builder.addCase(fetchSolutionsByPostId.pending, (state, action) => {
      const postId = action.meta.arg;
      if (!state.byPostId[postId]) {
        state.byPostId[postId] = { solutions: [], loading: true, loadingMore: false, error: null, lastUpdated: null, hasMore: true, lastCursor: null, pageSize: 10 };
      } else {
        state.byPostId[postId].loading = true;
        state.byPostId[postId].error = null;
      }
    });

    builder.addCase(fetchSolutionsByPostId.fulfilled, (state, action) => {
      const postId = action.meta.arg;
      if (state.byPostId[postId]) {
        state.byPostId[postId].solutions = (action.payload || []).map(s => ({
          ...s,
          shares: (s.shares || 0) + (state.pendingShareTicks[s.id] || 0)
        }));
        state.byPostId[postId].loading = false;
        state.byPostId[postId].loadingMore = false;
        state.byPostId[postId].lastUpdated = new Date().toISOString();
        // When using full subscription, disable hasMore to hide pagination controls
        state.byPostId[postId].hasMore = false;
        state.byPostId[postId].lastCursor = null;
      }
    });

    builder.addCase(fetchSolutionsByPostId.rejected, (state, action) => {
      const postId = action.meta.arg;
      if (state.byPostId[postId]) {
        state.byPostId[postId].loading = false;
        state.byPostId[postId].loadingMore = false;
        state.byPostId[postId].error = action.error.message || 'Failed to fetch solutions';
      }
    });

    // Handle manual update via the action dispatched in the thunk
    builder.addCase('solutions/updateSolutions', (state, action) => {
      const { postId, solutions } = action.payload;
      const guardedSolutions = (solutions || []).map(s => ({
        ...s,
        shares: (s.shares || 0) + (state.pendingShareTicks[s.id] || 0)
      }));
      if (state.byPostId[postId]) {
        state.byPostId[postId].solutions = guardedSolutions;
        state.byPostId[postId].lastUpdated = new Date().toISOString();
        state.byPostId[postId].hasMore = false;
        state.byPostId[postId].lastCursor = null;
      } else {
        state.byPostId[postId] = {
          solutions,
          loading: false,
          loadingMore: false,
          error: null,
          lastUpdated: new Date().toISOString(),
          hasMore: false,
          lastCursor: null,
          pageSize: 10
        };
      }
    });

    // Handle fetchSolutionById
    builder.addCase(fetchSolutionById.pending, (state) => {
      state.current.loading = true;
      state.current.error = null;
      state.current.solution = null;
    });
    builder.addCase(fetchSolutionById.fulfilled, (state, action) => {
      state.current.loading = false;
      state.current.error = null;
      state.current.solution = action.payload;
    });
    builder.addCase(fetchSolutionById.rejected, (state, action) => {
      state.current.loading = false;
      state.current.error = action.payload || action.error.message || 'Failed to fetch solution';
      state.current.solution = null;
    });

    // Handle fetchSavedSolutions
    builder.addCase(fetchSavedSolutions.pending, (state) => {
      state.saved.loading = true;
      state.saved.error = null;
    });
    builder.addCase(fetchSavedSolutions.fulfilled, (state, action) => {
      state.saved.loading = false;
      state.saved.error = null;
      state.saved.solutions = action.payload || [];
    });
    builder.addCase(fetchSavedSolutions.rejected, (state, action) => {
      state.saved.loading = false;
      state.saved.error = action.payload || action.error.message || 'Failed to fetch saved solutions';
      state.saved.solutions = [];
    });

    // Handle toggleShareSolution lifecycle to manage pending ticks

    // Use manual string matching if thunks aren't easily accessible as objects here, 
    // but they are imported at the top.

    builder.addCase(toggleShareSolution.fulfilled, (state, action) => {
      const { solutionId } = action.payload;
      if (state.pendingShareTicks[solutionId] > 0) {
        state.pendingShareTicks[solutionId] -= 1;
      }
    });
    builder.addCase(toggleShareSolution.rejected, (state, action) => {
      const { solutionId } = action.meta.arg || {};
      if (solutionId && state.pendingShareTicks[solutionId] > 0) {
        state.pendingShareTicks[solutionId] -= 1;
      }
    });

  }
});

// Export actions
export const {
  clearSolutionsForPost,
  clearCurrentSolution,
  setUpvoteStatus,
  setLikeStatus,
  setSaveStatus,
  setShareStatus,
  addToSavedSolutions,
  removeFromSavedSolutions,
  updateSolutionField,
  addPhase,
  updatePhase,
  addTask,
  updateTask,
  removePhase,
  removeTask,
  resetBuilder,
  resetSolutions,
  setUnsubscribe
} = solutionSlice.actions;

export default solutionSlice.reducer;

// Selector for the solution builder state
export const selectSolutionBuilder = createSelector(
  [
    state => state.solutions.builder.solution,
    state => state.solutions.builder.loading,
    state => state.solutions.builder.error,
    state => state.solutions.builder.phases,
    state => state.solutions.builder.currentPhaseIndex
  ],
  (solution, loading, error, phases, currentPhaseIndex) => {
    const displayPhases = Array.isArray(phases) ? phases.map(phase => ({
      ...phase,
      tasks: Array.isArray(phase.tasks) ? phase.tasks.map(task => ({
        ...task,
        description: task.description || ''
      })) : []
    })) : [];

    return {
      solution: solution || { title: '', type: '', description: '' },
      loading: Boolean(loading),
      error,
      displayPhases,
      currentPhaseIndex: Math.max(0, currentPhaseIndex || 0)
    };
  }
);
